import { useEffect, useState, useCallback } from "react";
import { X, Users, RefreshCcw, AlertCircle, Building2, Home, User } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { AsambleaRecord } from "../../../../api/asambleaService";
import {
  obtenerParticipantesAsamblea,
  ParticipanteAsambleaRecord,
} from "../../../../api/participanteAsambleaService";

interface ParticipantesAsambleaModalProps {
  isOpen: boolean;
  asamblea: AsambleaRecord | null;
  onClose: () => void;
}

const tipoUsuarioStyle: Record<string, string> = {
  PR: "bg-green-100 text-green-700 border border-green-200",
  IN: "bg-blue-100 text-blue-700 border border-blue-200",
  FA: "bg-purple-100 text-purple-700 border border-purple-200",
};

const getTipoStyle = (tipo: string) =>
  tipoUsuarioStyle[tipo?.toUpperCase()] ||
  "bg-gray-100 text-gray-700 border border-gray-200";

const formatFecha = (value: string) => {
  try {
    return new Date(value).toLocaleString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

export default function ParticipantesAsambleaModal({
  isOpen,
  asamblea,
  onClose,
}: ParticipantesAsambleaModalProps) {
  const [participantes, setParticipantes] = useState<ParticipanteAsambleaRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchParticipantes = useCallback(async () => {
    if (!asamblea) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await obtenerParticipantesAsamblea({
        asamblea_codigo: String(asamblea.asa_codigo),
        llave: asamblea.asa_llave || "",
      });
      if (response.code === "1" && Array.isArray(response.object_)) {
        setParticipantes(response.object_);
      } else {
        setParticipantes([]);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudieron cargar los participantes"
      );
    } finally {
      setIsLoading(false);
    }
  }, [asamblea]);

  useEffect(() => {
    if (isOpen && asamblea) {
      fetchParticipantes();
    } else {
      setParticipantes([]);
      setError(null);
    }
  }, [isOpen, asamblea, fetchParticipantes]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl flex flex-col max-h-[92vh] sm:max-h-[90vh]">

        {/* Header */}
        <div className="px-4 sm:px-6 pt-4 pb-3 border-b">
          {/* Row 1: ícono + título + botón cierre */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="bg-military-100 p-2 rounded-full flex-shrink-0">
                <Users size={18} className="text-military-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-gray-900 leading-tight">
                  Participantes
                </h2>
                {asamblea && (
                  <p className="text-xs text-gray-500 truncate">
                    #{asamblea.asa_codigo} — {asamblea.asa_titulo}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 mt-0.5"
            >
              <X size={18} />
            </button>
          </div>

          {/* Row 2: botón actualizar + contador */}
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">
              {!isLoading && !error
                ? `${participantes.length} participante${participantes.length !== 1 ? "s" : ""}`
                : ""}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchParticipantes}
              disabled={isLoading}
              className="gap-1 text-xs h-7 px-2"
            >
              <RefreshCcw size={13} className={isLoading ? "animate-spin" : ""} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
              <p className="text-sm text-gray-600">Cargando participantes...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-red-500 gap-3 text-center">
              <AlertCircle size={36} />
              <p className="text-sm font-medium">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchParticipantes}>
                Reintentar
              </Button>
            </div>
          ) : participantes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3 text-center">
              <Users size={44} />
              <p className="text-gray-600 font-medium text-sm">
                No hay participantes registrados
              </p>
              <p className="text-xs text-gray-500">
                Aún no se han unido participantes a esta asamblea.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {participantes.map((p) => (
                <div
                  key={p.pas_codigo}
                  className="rounded-xl border bg-gray-50 hover:bg-white transition p-3 shadow-sm"
                >
                  {/* Fila principal */}
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-military-100 flex items-center justify-center">
                      <User size={16} className="text-military-600" />
                    </div>

                    {/* Info central */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                        <span className="font-semibold text-sm text-gray-900 truncate max-w-[160px] sm:max-w-none">
                          {p.pas_usuario_nombre}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${getTipoStyle(
                            p.pas_tipo_usuario
                          )}`}
                        >
                          {p.pas_tipo_usuario_nombre}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{p.pas_usuario}</p>

                      {/* Torre / Apto + Fecha en fila flex-wrap */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                        <span className="flex items-center gap-1 text-xs text-gray-600">
                          <Building2 size={11} />
                          Torre {p.pas_torre_nombre}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-600">
                          <Home size={11} />
                          Apto {p.pas_apartamento_nombre}
                        </span>
                        <span className="text-xs text-gray-400 sm:ml-auto">
                          {formatFecha(p.pas_fecha)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose} className="w-full sm:w-auto">
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
